import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';

const buttonVariants = cva(
	"inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
	{
		variants: {
			variant: {
				default: 'bg-primary text-white hover:opacity-90',
				secondary: 'bg-secondary text-secondary-foreground hover:opacity-90',
				outline: 'border border-gray-300 bg-white text-secondary hover:bg-gray-50',
				ghost: 'bg-transparent hover:bg-gray-100 text-secondary',
				link: 'bg-transparent underline text-primary hover:opacity-80',
				gradient: 'bg-gradient-to-r from-primary via-babyblue to-royalpurple text-white hover:opacity-90',
			},
			size: {
				sm: 'h-9 px-3',
				md: 'h-10 px-4',
				lg: 'h-11 px-6',
			},
		},
		defaultVariants: {
			variant: 'default',
			size: 'md',
		},
	}
);

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	({ className, variant, size, asChild = false, ...props }, ref) => {
		const Comp = asChild ? Slot : 'button';
		return (
			<Comp
				className={twMerge(buttonVariants({ variant, size }), className)}
				ref={ref as any}
				{...props}
			/>
		);
	}
);

Button.displayName = 'Button';

export default Button;
